// src/types/schoolConfig.ts
// TypeScript types for school configuration

/**
 * School Configuration - Global settings for the school
 * Stored in school_configuration table (single row)
 */
export interface SchoolConfiguration {
  id: string;

  // School Identity
  schoolName: string;
  schoolShortName: string;
  schoolAddress: string;
  schoolPhone: string;
  schoolEmail: string;
  schoolWebsite: string;
  schoolTagline: string;
  principalName: string;
  logoUrl: string | null;
  bannerUrl: string;
  timezone: string;

  // Load Calculation Settings
  baseMinutesPerFortnight: number;  // Default: 2220
  defaultMinutesPerLine: number;     // Default: 440

  // Year Levels
  yearLevels: number[];

  // Menu Visibility
  hiddenMenuItems: string[];

  // Metadata
  createdAt: string;
  updatedAt: string;
  updatedBy: string | null;
}

/**
 * Database row format (snake_case)
 */
export interface SchoolConfigurationRow {
  id: string;
  school_name: string;
  school_short_name: string;
  school_address: string;
  school_phone: string;
  school_email: string;
  school_website: string;
  school_tagline: string;
  principal_name: string;
  logo_url: string | null;
  banner_url: string;
  timezone: string;
  base_minutes_per_fortnight: number;
  default_minutes_per_line: number;
  year_levels: number[];
  hidden_menu_items: string[];
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

/**
 * Curriculum Area - Key Learning Area / Faculty definition
 * Stored in curriculum_areas table
 */
export interface CurriculumArea {
  id: string;
  code: string;       // e.g., 'ENG', 'MAT', 'HPE'
  name: string;       // e.g., 'English', 'Mathematics'
  color: string;
  description: string | null;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Database row format for curriculum_areas
 */
export interface CurriculumAreaRow {
  id: string;
  code: string;
  name: string;
  color: string;
  description: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Level Code - Year level / course level definition
 * Stored in level_codes table
 */
export interface LevelCode {
  id: string;
  code: string;         // e.g., 'DI', 'EN', '12', '34'
  name: string;         // e.g., 'Discover', 'Units 1 & 2'
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Database row format for level_codes
 */
export interface LevelCodeRow {
  id: string;
  code: string;
  name: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Enhanced Grid Type with full configuration
 */
export interface ConfigurableGridType {
  id: string;
  name: string;
  description: string | null;
  defaultClassMinutes: number;
  isSystemDefault: boolean;
  lineStructure: string[];       // Available lines e.g., ['A','B','C',...]
  linePairings: Record<string, string>;  // e.g., {'A':'G', 'B':'H', ...}
  fullYear: boolean;             // Whether lines run full year (vs semester split)
  periodsPerDay: number;
  daysPerCycle: number;
  structure: Record<string, unknown>;  // Legacy structure field
  schoolId: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Database row format for grid_types
 */
export interface GridTypeRow {
  id: string;
  name: string;
  description: string | null;
  default_class_minutes: number;
  is_system_default: boolean;
  line_structure: string[];
  line_pairings: Record<string, string>;
  full_year: boolean;
  periods_per_day: number;
  days_per_cycle: number;
  structure: Record<string, unknown>;
  school_id: string | null;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Conversion utilities
// ============================================================================

/**
 * Convert database row to SchoolConfiguration
 */
export function rowToSchoolConfig(row: SchoolConfigurationRow): SchoolConfiguration {
  return {
    id: row.id,
    schoolName: row.school_name,
    schoolShortName: row.school_short_name,
    schoolAddress: row.school_address,
    schoolPhone: row.school_phone,
    schoolEmail: row.school_email,
    schoolWebsite: row.school_website,
    schoolTagline: row.school_tagline,
    principalName: row.principal_name,
    logoUrl: row.logo_url,
    bannerUrl: row.banner_url,
    timezone: row.timezone,
    baseMinutesPerFortnight: row.base_minutes_per_fortnight,
    defaultMinutesPerLine: row.default_minutes_per_line,
    yearLevels: row.year_levels,
    hiddenMenuItems: row.hidden_menu_items || [],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    updatedBy: row.updated_by,
  };
}

/**
 * Convert SchoolConfiguration to database row format (for updates)
 */
export function schoolConfigToRow(config: Partial<SchoolConfiguration>): Partial<SchoolConfigurationRow> {
  const row: Partial<SchoolConfigurationRow> = {};

  if (config.schoolName !== undefined) row.school_name = config.schoolName;
  if (config.schoolShortName !== undefined) row.school_short_name = config.schoolShortName;
  if (config.schoolAddress !== undefined) row.school_address = config.schoolAddress;
  if (config.schoolPhone !== undefined) row.school_phone = config.schoolPhone;
  if (config.schoolEmail !== undefined) row.school_email = config.schoolEmail;
  if (config.schoolWebsite !== undefined) row.school_website = config.schoolWebsite;
  if (config.schoolTagline !== undefined) row.school_tagline = config.schoolTagline;
  if (config.principalName !== undefined) row.principal_name = config.principalName;
  if (config.logoUrl !== undefined) row.logo_url = config.logoUrl;
  if (config.bannerUrl !== undefined) row.banner_url = config.bannerUrl;
  if (config.timezone !== undefined) row.timezone = config.timezone;
  if (config.baseMinutesPerFortnight !== undefined) row.base_minutes_per_fortnight = config.baseMinutesPerFortnight;
  if (config.defaultMinutesPerLine !== undefined) row.default_minutes_per_line = config.defaultMinutesPerLine;
  if (config.yearLevels !== undefined) row.year_levels = config.yearLevels;
  if (config.hiddenMenuItems !== undefined) row.hidden_menu_items = config.hiddenMenuItems;

  return row;
}

/**
 * Convert database row to CurriculumArea
 */
export function rowToCurriculumArea(row: CurriculumAreaRow): CurriculumArea {
  return {
    id: row.id,
    code: row.code,
    name: row.name,
    color: row.color,
    description: row.description,
    displayOrder: row.display_order,
    isActive: row.is_active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/**
 * Convert CurriculumArea to database row format
 */
export function curriculumAreaToRow(area: Partial<CurriculumArea>): Partial<CurriculumAreaRow> {
  const row: Partial<CurriculumAreaRow> = {};

  if (area.code !== undefined) row.code = area.code;
  if (area.name !== undefined) row.name = area.name;
  if (area.color !== undefined) row.color = area.color;
  if (area.description !== undefined) row.description = area.description;
  if (area.displayOrder !== undefined) row.display_order = area.displayOrder;
  if (area.isActive !== undefined) row.is_active = area.isActive;

  return row;
}

/**
 * Convert database row to LevelCode
 */
export function rowToLevelCode(row: LevelCodeRow): LevelCode {
  return {
    id: row.id,
    code: row.code,
    name: row.name,
    description: row.description,
    sortOrder: row.sort_order,
    isActive: row.is_active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/**
 * Convert LevelCode to database row format
 */
export function levelCodeToRow(level: Partial<LevelCode>): Partial<LevelCodeRow> {
  const row: Partial<LevelCodeRow> = {};

  if (level.code !== undefined) row.code = level.code;
  if (level.name !== undefined) row.name = level.name;
  if (level.description !== undefined) row.description = level.description;
  if (level.sortOrder !== undefined) row.sort_order = level.sortOrder;
  if (level.isActive !== undefined) row.is_active = level.isActive;

  return row;
}

/**
 * Convert database row to ConfigurableGridType
 */
export function rowToGridType(row: GridTypeRow): ConfigurableGridType {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    defaultClassMinutes: row.default_class_minutes,
    isSystemDefault: row.is_system_default,
    lineStructure: row.line_structure || [],
    linePairings: row.line_pairings || {},
    fullYear: row.full_year,
    periodsPerDay: row.periods_per_day,
    daysPerCycle: row.days_per_cycle,
    structure: row.structure || {},
    schoolId: row.school_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ============================================================================
// Default values (fallbacks when database is unavailable)
// ============================================================================

export const DEFAULT_SCHOOL_CONFIG: SchoolConfiguration = {
  id: '00000000-0000-0000-0000-000000000001',
  schoolName: 'Salesian College Sunbury',
  schoolShortName: 'SCS',
  schoolAddress: 'Sunbury 3429, Vic, Australia',
  schoolPhone: '03 9744 6182',
  schoolEmail: 'info@scrvic.edu.au',
  schoolWebsite: 'scrvic.edu.au',
  schoolTagline: 'Engaging with Community | Passion for Learning | Striving for Excellence | Inspired by Faith',
  principalName: 'Mark Brockhus',
  logoUrl: null,
  bannerUrl: '/pageheader.jpg',
  timezone: 'Australia/Melbourne',
  baseMinutesPerFortnight: 2220,
  defaultMinutesPerLine: 440,
  yearLevels: [7, 8, 9, 10, 11, 12],
  hiddenMenuItems: [],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  updatedBy: null,
};

// Legacy constant exports for backward compatibility
export const DEFAULT_BASE_MINUTES_PER_FORTNIGHT = 2220;
export const DEFAULT_MINUTES_PER_LINE = 440;
